/**
 * Typography Configuration
 *
 * Centralized typography styles for consistent text styling across the application.
 * Defines font sizes, weights, colors, and spacing for all text elements.
 */

// ============================================================================
// PRIMARY HEADING STYLES
// ============================================================================

/**
 * Primary heading styles for main page titles and hero sections
 * Usage: Hero titles, main page headings, section titles
 */
export const PRIMARY_HEADINGS = {
  // Main hero headings (Home, Business, Magazine pages)
  hero: "text-4xl md:text-5xl font-bold text-white leading-tight",

  // Large section headings
  section: "text-4xl md:text-5xl font-bold text-white leading-tight",

  // Product/Article titles
  product: "text-4xl md:text-6xl font-bold text-white leading-tight",

  // Sub-section headings
  subsection: "text-3xl md:text-4xl font-bold text-white",

  // Card/Component titles
  card: "text-2xl font-semibold text-white",

  // Feature/Item titles
  feature: "text-xl font-semibold text-white",
};

/**
 * Primary heading with responsive margin bottom
 */
export const PRIMARY_HEADING_WITH_MARGIN = {
  hero: `${PRIMARY_HEADINGS.hero} mb-8`,
  section: `${PRIMARY_HEADINGS.section} mb-6`,
  product: `${PRIMARY_HEADINGS.product} mb-6`,
  subsection: `${PRIMARY_HEADINGS.subsection} mb-6`,
  card: `${PRIMARY_HEADINGS.card} mb-4`,
  feature: `${PRIMARY_HEADINGS.feature} mb-3`,
};

// ============================================================================
// SUPPORTING TEXT STYLES
// ============================================================================

/**
 * Supporting text styles for descriptions, abstracts, and body content
 * Usage: Product descriptions, page subtitles, content paragraphs
 */
export const SUPPORTING_TEXT = {
  // Hero/Page subtitles - larger supporting text
  subtitle: "text-xl md:text-2xl text-gray-300 leading-relaxed",

  // Standard supporting text for descriptions
  description: "text-xl text-gray-300 leading-relaxed",

  // Medium supporting text
  medium: "text-lg text-gray-300 leading-relaxed",

  // Standard body text
  body: "text-base text-gray-300 leading-relaxed",

  // Small supporting text
  small: "text-sm text-gray-300",

  // Placeholder text
  placeholder: "text-lg text-gray-400",

  // Secondary text (lighter color)
  secondary: "text-base text-gray-400 leading-relaxed",
};

/**
 * Supporting text with responsive margins and max-width constraints
 */
export const SUPPORTING_TEXT_WITH_LAYOUT = {
  subtitle: `${SUPPORTING_TEXT.subtitle} max-w-4xl mx-auto mb-12`,
  description: `${SUPPORTING_TEXT.description} max-w-3xl mx-auto mb-6`,
  medium: `${SUPPORTING_TEXT.medium} max-w-3xl mx-auto mb-6`,
  body: `${SUPPORTING_TEXT.body} mb-4`,
  small: `${SUPPORTING_TEXT.small} mb-2`,
};

// ============================================================================
// SPECIALIZED TYPOGRAPHY STYLES
// ============================================================================

/**
 * Button text styles for different button types
 */
export const BUTTON_TEXT = {
  primary: "text-lg font-semibold",
  secondary: "text-lg font-medium",
  small: "text-base font-medium",
  tiny: "text-sm font-medium",
};

/**
 * Navigation and UI element text styles
 */
export const UI_TEXT = {
  nav: "text-base font-medium",
  badge: "text-sm font-semibold",
  tag: "text-xs font-medium",
  label: "text-sm font-medium",
  caption: "text-xs text-gray-400",
};

/**
 * Interactive text styles (links, hover states)
 */
export const INTERACTIVE_TEXT = {
  link: "text-violet-400 hover:text-violet-300 transition-colors duration-300",
  linkUnderline:
    "text-violet-400 hover:text-violet-300 underline transition-colors duration-300",
  hoverHighlight: "group-hover:text-violet-300 transition-colors duration-300",
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get primary heading style by type
 * @param {string} type - The heading type (hero, section, product, etc.)
 * @param {boolean} withMargin - Whether to include margin bottom
 * @returns {string} - The CSS class string
 */
export const getPrimaryHeading = (type = "hero", withMargin = true) => {
  const baseStyle = PRIMARY_HEADINGS[type] || PRIMARY_HEADINGS.hero;

  if (withMargin) {
    return PRIMARY_HEADING_WITH_MARGIN[type] || `${baseStyle} mb-6`;
  }

  return baseStyle;
};

/**
 * Get supporting text style by type
 * @param {string} type - The text type (subtitle, description, body, etc.)
 * @param {boolean} withLayout - Whether to include layout constraints
 * @returns {string} - The CSS class string
 */
export const getSupportingText = (type = "description", withLayout = false) => {
  const baseStyle = SUPPORTING_TEXT[type] || SUPPORTING_TEXT.description;

  if (withLayout && SUPPORTING_TEXT_WITH_LAYOUT[type]) {
    return SUPPORTING_TEXT_WITH_LAYOUT[type];
  }

  return baseStyle;
};

/**
 * Create a complete heading with optional custom highlight color
 * @param {string} headingType - Type of heading (hero, section, etc.)
 * @param {string} highlightClass - Custom highlight class for colored text
 * @returns {string} - Combined CSS classes
 */
export const createStyledHeading = (
  headingType = "hero",
  highlightClass = "",
) => {
  const baseHeading = getPrimaryHeading(headingType, true);
  return highlightClass ? `${baseHeading} ${highlightClass}` : baseHeading;
};

/**
 * Text center utility for centered text layouts
 */
export const TEXT_CENTER = "text-center";

/**
 * Responsive text alignment utilities
 */
export const TEXT_ALIGNMENT = {
  center: "text-center",
  left: "text-left",
  right: "text-right",
  justify: "text-justify",
};

// ============================================================================
// EXPORT ALL STYLES AS DEFAULT
// ============================================================================

const TypographyConfig = {
  PRIMARY_HEADINGS,
  PRIMARY_HEADING_WITH_MARGIN,
  SUPPORTING_TEXT,
  SUPPORTING_TEXT_WITH_LAYOUT,
  BUTTON_TEXT,
  UI_TEXT,
  INTERACTIVE_TEXT,
  TEXT_ALIGNMENT,
  TEXT_CENTER,

  // Utility functions
  getPrimaryHeading,
  getSupportingText,
  createStyledHeading,
};

export default TypographyConfig;
